import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MuiTelInput } from 'mui-tel-input';

import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';

import { AUTO_CREATE_LOGISTIC_MODES_OPTIONS, PLATFORM_NAME, SKU_SOURCE_OPTIONS } from './constants';
import useMLFulfillConfig from './useMLFulfillConfig';

interface MLFulfillConfigProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  pluginId: string;
  clientId: string;
  activeMlCount: number;
  webhookPlatformOwner: string | null;
  onSaved?: () => void;
}

const MLFulfillConfig = (props: MLFulfillConfigProps) => {
  const theme = useTheme();
  const hooks = useMLFulfillConfig(props);

  const onSave = async () => {
    const success = await hooks.onSaveConfig();
    if (success) {
      props.onSaved?.();
      props.onClose();
    }
  };

  const isPrimaryDisabled =
    Boolean(props.webhookPlatformOwner) && props.webhookPlatformOwner !== PLATFORM_NAME;

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={`Configuración de conexión ${props.name}`}
      maxWidth="lg"
      actions={
        <Button variant="contained" onClick={onSave} disabled={hooks.saving}>
          {hooks.saving ? 'Guardando...' : 'Guardar'}
        </Button>
      }
    >
      {hooks.loading ? (
        <CircularProgress size={24} />
      ) : (
        <Grid container size={12} flexDirection="column" spacing={2}>
          <Typography variant="body1" color={theme.palette.grey[500]} fontWeight="bold">
            Información de origen
          </Typography>
          <div className="w-full px-4">
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Nombre de tienda"
                  value={hooks.storeInfo.storeName}
                  onChange={(e) => hooks.onChangeStoreInfo('storeName', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Email de tienda"
                  value={hooks.storeInfo.storeEmail}
                  onChange={(e) => hooks.onChangeStoreInfo('storeEmail', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={12}>
                <MuiTelInput
                  fullWidth
                  size="small"
                  label="Teléfono de tienda"
                  defaultCountry="MX"
                  onlyCountries={['MX', 'CO', 'CL', 'PE']}
                  value={hooks.storeInfo.storePhone}
                  onChange={hooks.onChangePhone}
                  error={hooks.phoneError}
                  helperText={hooks.phoneError ? 'Ingresa un número válido' : undefined}
                />
              </Grid>
            </Grid>
          </div>

          <Typography variant="body1" color={theme.palette.grey[500]} mt={2} fontWeight="bold">
            Configuraciones adicionales
          </Typography>
          <div className="flex flex-col gap-3 px-4">
            <FormControlLabel
              sx={{ gap: '1rem' }}
              control={
                <Checkbox
                  color="primary"
                  name="createOrders"
                  checked={hooks.autoCreateOrder}
                  onChange={(e) => hooks.onChangeAutoCreateOrder(e.target.checked)}
                />
              }
              label={
                <Typography color={theme.palette.grey[500]}>
                  Creación automática de orden
                </Typography>
              }
            />

            {hooks.autoCreateOrder && (
              <div className="w-full pl-2">
                <Autocomplete
                  multiple
                  id="auto-create-logistic-modes-select"
                  options={AUTO_CREATE_LOGISTIC_MODES_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={AUTO_CREATE_LOGISTIC_MODES_OPTIONS.filter((option) =>
                    hooks.autoCreateLogisticModes.includes(option.value),
                  )}
                  onChange={(_event, newValue) => {
                    hooks.onChangeLogisticModes(newValue.map((item) => item.value));
                  }}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip key={key} label={option.label} size="small" {...tagProps} />;
                    })
                  }
                  renderOption={(optionProps, option) => {
                    const { key, ...otherProps } = optionProps;
                    return (
                      <li key={key} {...otherProps}>
                        <div className="flex flex-col py-1">
                          <Typography variant="body2" fontWeight="medium">
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Modos logísticos permitidos para creación automática"
                      placeholder={
                        hooks.autoCreateLogisticModes.length === 0
                          ? 'Selecciona modos logísticos'
                          : ''
                      }
                      helperText="Selecciona qué tipos de envíos de Mercado Libre deben crear guías automáticamente en 99minutos. Si no seleccionas ninguno, las órdenes solo se guardarán como pendientes para creación manual."
                    />
                  )}
                />
              </div>
            )}

            <div className="mt-2 w-full">
              <TextField
                select
                fullWidth
                size="small"
                label="Identificador de producto para SKU en Fulfill"
                value={hooks.skuSource}
                onChange={(e) => hooks.onChangeSkuSource(e.target.value)}
                helperText="Selecciona qué código de Mercado Libre se envía como SKU a 99minutos Fulfill para descontar y preparar el inventario en bodega."
              >
                {SKU_SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <div className="flex flex-col py-0.5">
                      <Typography variant="body2" fontWeight="medium">
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </div>
                  </MenuItem>
                ))}
              </TextField>
            </div>

            {props.activeMlCount > 1 && (
              <Tooltip
                title={
                  isPrimaryDisabled
                    ? 'Desactiva esta opción en la plataforma primaria actual antes de cambiarla'
                    : 'Si tienes varias plataformas activas, esta se priorizará para la creación automática de órdenes'
                }
                placement="right"
              >
                <span>
                  <FormControlLabel
                    sx={{ gap: '1rem' }}
                    control={
                      <Checkbox
                        color="primary"
                        checked={hooks.isWebhookPrimary}
                        onChange={(e) => hooks.onChangeIsWebhookPrimary(e.target.checked)}
                        disabled={isPrimaryDisabled}
                      />
                    }
                    label={
                      <Typography
                        color={
                          isPrimaryDisabled ? theme.palette.grey[400] : theme.palette.grey[500]
                        }
                      >
                        Plataforma primaria
                      </Typography>
                    }
                  />
                </span>
              </Tooltip>
            )}
          </div>
        </Grid>
      )}
    </Modal>
  );
};

export default MLFulfillConfig;
